import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import AdminLayout from "../../Layout/index.jsx";
import NetworkIndicator from "../../../Practice/Components/NetworkIndicator.jsx";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Checkbox, Label, TextInput, Textarea } from "flowbite-react";
import {
  useCreateAssignmentMutation,
  useGetAllFoldersLazyQuery,
  useUpdateFolderMutation,
} from "../../../../graphql/generated.tsx";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ToastUI, { ToastContainer } from "../../../Assignments/Components/ToastUI.jsx";

const CreateAssignment = () => {
  const navigate = useNavigate();
  const [createAssignment] = useCreateAssignmentMutation();
  const [updateFolder] = useUpdateFolderMutation();
  const [fetchFolders, { data: foldersData, loading: foldersLoading }] =
    useGetAllFoldersLazyQuery();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    setToasts((list) => [...list, { id, type, message }]);
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    short_description: "",
    description: "",
    start_time: null,
    due_date: null,
    duration_hours: 0,
    duration_minutes: 30,
    languages: [],
    domain: "",
    max_tabSwitches: 0,
    max_cheat_count: 0,
    enable_proctoring: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleLanguageChange = (selectedOptions) => {
    const selectedLanguages = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];

    setFormData({
      ...formData,
      languages: selectedLanguages,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return; // Prevent double click
    setIsSubmitting(true);

    try {
      const startTime = formData.start_time instanceof Date ? formData.start_time : null;
      const dueDate = formData.due_date instanceof Date ? formData.due_date : null;

      if (!startTime || Number.isNaN(startTime.getTime())) {
        pushToast("warning", "Please select a valid start date & time");
        setIsSubmitting(false);
        return;
      }

      if (!dueDate || Number.isNaN(dueDate.getTime())) {
        pushToast("warning", "Please select a valid due date & time");
        setIsSubmitting(false);
        return;
      }

      if (startTime.getTime() >= dueDate.getTime()) {
        pushToast("warning", "Due date must be after start time");
        setIsSubmitting(false);
        return;
      }

      const enableProctoring = Boolean(formData.enable_proctoring);
      const maxCheatCount = enableProctoring ? parseInt(formData.max_cheat_count) : 0;
      const maxTabSwitches = enableProctoring ? parseInt(formData.max_tabSwitches) : 0;
      const durationHours = Number(formData.duration_hours) || 0;
      const durationMinutes = Number(formData.duration_minutes) || 0;

      if (
        durationHours < 0 ||
        durationHours > 24 ||
        durationMinutes < 0 ||
        durationMinutes > 60
      ) {
        pushToast(
          "warning",
          "Please enter a valid duration (hours: 0-24, minutes: 0-60)"
        );
        setIsSubmitting(false);
        return;
      }

      if (durationHours === 0 && durationMinutes === 0) {
        pushToast("warning", "Duration should be greater than 0");
        setIsSubmitting(false);
        return;
      }

      if (!selectedFolderId) {
        pushToast("warning", "Please select an assignment category");
        setIsSubmitting(false);
        return;
      }

      const totalDurationInHours = durationHours + durationMinutes / 60;

      const { data: createData } = await createAssignment({
        variables: {
          createAssignmentInput: {
            name: formData.name,
            short_description: formData.short_description,
            description: formData.description,
            duration: totalDurationInHours,
            start_time: startTime,
            due_date: dueDate,
            languages: formData.languages,
            domain: formData.domain,
            max_tabSwitches: maxTabSwitches,
            max_cheat_count: maxCheatCount,
            enable_proctoring: enableProctoring,
          },
        },
      });

      const newAssignmentId =
        createData?.createAssignment?.id || createData?.createAssignment?._id;

      if (!newAssignmentId) {
        pushToast("error", "Assignment created but id was not returned");
        setIsSubmitting(false);
        return;
      }

      if (selectedFolderId) {
        try {
          if (!folders.length) {
            const { data: freshFolders } = await fetchFolders();
            const list = freshFolders?.getAllFolders ?? [];
            const folder = list.find(
              (f) => (f.id || f._id) === selectedFolderId
            );
            if (!folder) {
              throw new Error("Selected folder not found");
            }
            const folderId = folder.id || folder._id;
            const existingAssignments = folder.assignments ?? [];
            await updateFolder({
              variables: {
                id: folderId,
                updateFolderDto: {
                  name: folder.name,
                  assignments: existingAssignments.includes(newAssignmentId)
                    ? existingAssignments
                    : [...existingAssignments, newAssignmentId],
                },
              },
            });
          } else {
            await addAssignmentToFolder(newAssignmentId);
          }
          pushToast(
            "success",
            "Assignment created and added to folder successfully"
          );
        } catch (folderError) {
          console.error(folderError);
          pushToast(
            "warning",
            "Assignment created, but could not add it to the selected folder"
          );
        }
      } else {
        pushToast("success", "Assignment created successfully");
      }

      setTimeout(() => navigate("/admin/assignment-folders"), 900);
    } catch (error) {
      pushToast("error", "Error creating assignment");
      console.error(error);
      setIsSubmitting(false); // allow retry
    }
  };

  const languageOptions = [
    { value: "Python", label: "Python" },
    { value: "C", label: "C" },
    { value: "Java", label: "Java" },
    { value: "Cpp", label: "Cpp" },
  ];

  const folders = foldersData?.getAllFolders ?? [];
  const folderOptions = folders.map((folder) => ({
    value: folder.id || folder._id,
    label: folder.name,
  }));

  const handleFolderMenuOpen = () => {
    fetchFolders();
  };

  const handleFolderChange = (selectedOption) => {
    setSelectedFolderId(selectedOption?.value ?? "");
  };

  const addAssignmentToFolder = async (assignmentId) => {
    const folder = folders.find(
      (f) => (f.id || f._id) === selectedFolderId
    );
    if (!folder) {
      throw new Error("Selected folder not found");
    }

    const folderId = folder.id || folder._id;
    const existingAssignments = folder.assignments ?? [];
    const assignments = existingAssignments.includes(assignmentId)
      ? existingAssignments
      : [...existingAssignments, assignmentId];

    await updateFolder({
      variables: {
        id: folderId,
        updateFolderDto: {
          name: folder.name,
          assignments,
        },
      },
    });
  };

  return (
    <AdminLayout>
      <NetworkIndicator />
      <div className="p-8 max-md:p-5">
        <div className="flex items-center mb-6">
          <button
            onClick={() => window.history.back()}
            className="text-gray-500 hover:text-gray-700 focus:outline-none w-[100px]"
          >
            <span className="gap-2 text-sm font-medium flex items-center">
              <IoMdArrowRoundBack className="h-6 w-6" />
              Go Back
            </span>
          </button>
        </div>

        <h2 className="text-3xl font-bold text-center w-full max-md:text-xl mb-4">
          Create Assignment
        </h2>

        <form
          onSubmit={handleSubmit}
          className="max-w-2xl mx-auto bg-white rounded-md shadow-md p-8"
        >
          <Label className="font-semibold mb-2" htmlFor="name" value="Name:" />
          <TextInput
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full mb-4"
            required
          />

          <Label
            className="font-semibold mb-2"
            htmlFor="short_description"
            value="Short Description:"
          />
          <Textarea
            id="short_description"
            name="short_description"
            value={formData.short_description}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-md mb-4"
            required
          />

          <Label
            className="font-semibold mb-2"
            htmlFor="description"
            value="Description:"
          />
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-md mb-4"
            required
          />

          <div className="mb-5">
            <Label
              className="font-semibold mb-2"
              htmlFor="duration"
              value="Duration:"
            />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-start">
              <div>
                <Label
                  className="font-semibold mb-2"
                  htmlFor="duration_hours"
                  value="Hours"
                />
                <TextInput
                  type="number"
                  id="duration_hours"
                  name="duration_hours"
                  value={formData.duration_hours}
                  onChange={handleChange}
                  min={0}
                  max={24}
                  className="w-full"
                  required
                />
              </div>
              <div>
                <Label
                  className="font-semibold mb-2"
                  htmlFor="duration_minutes"
                  value="Minutes"
                />
                <TextInput
                  type="number"
                  id="duration_minutes"
                  name="duration_minutes"
                  value={formData.duration_minutes}
                  onChange={handleChange}
                  min={0}
                  max={60}
                  className="w-full"
                  required
                />
              </div>
              <div className="md:-mt-1">
                <Label
                  className="font-semibold mb-2"
                  htmlFor="languages"
                  value="Languages:"
                />
                <Select
                  id="languages"
                  name="languages"
                  options={languageOptions}
                  isMulti
                  onChange={handleLanguageChange}
                  className="w-full"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: 42,
                      borderRadius: 8,
                    }),
                  }}
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label
                className="font-semibold mb-2"
                htmlFor="start_time"
                value="Start Date & Time:"
              />
              <div className="w-full mb-4">
                <ReactDatePicker
                  selected={formData.start_time}
                  onChange={(date) =>
                    setFormData((prev) => {
                      const next = { ...prev, start_time: date };
                      if (
                        date &&
                        prev.due_date instanceof Date &&
                        prev.due_date.getTime() < date.getTime()
                      ) {
                        next.due_date = null;
                      }
                      return next;
                    })
                  }
                  showTimeSelect
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="MMM d, yyyy h:mm aa"
                  placeholderText="Select start"
                  className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  calendarClassName="bt-calendar"
                  required
                />
              </div>
            </div>
            <div>
              <Label
                className="font-semibold mb-2"
                htmlFor="due_date"
                value="Due Date & Time:"
              />
              <div className="w-full mb-4">
                <ReactDatePicker
                  selected={formData.due_date}
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      due_date: date,
                    }))
                  }
                  showTimeSelect
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="MMM d, yyyy h:mm aa"
                  placeholderText="Select due date"
                  minDate={formData.start_time || undefined}
                  className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  calendarClassName="bt-calendar"
                  required
                />
              </div>
            </div>
          </div>

          <div className="mb-5">
            <Checkbox
              name="enable_proctoring"
              id="enableProctoring"
              checked={formData.enable_proctoring}
              onChange={handleChange}
            />
            <Label
              htmlFor="enable_proctoring"
              value="Enable Proctoring"
              className="ml-4"
            />
          </div>

          <Label
            className="font-semibold mb-2"
            htmlFor="max_cheat_count"
            value="Maximum cheat count:"
          />
          <TextInput
            type="number"
            id="max_cheat_count"
            name="max_cheat_count"
            value={formData.max_cheat_count}
            onChange={handleChange}
            className="w-full mb-4"
            required
          />

          <Label
            className="font-semibold mb-2"
            htmlFor="max_tabSwitches"
            value="Maximum Tab Switches:"
          />
          <TextInput
            type="number"
            id="max_tabSwitches"
            name="max_tabSwitches"
            value={formData.max_tabSwitches}
            onChange={handleChange}
            className="w-full mb-4"
            required
          />

          

          <Label
            className="font-semibold mb-2"
            htmlFor="domain"
            value="Domain:"
          />
          <TextInput
            type="text"
            id="domain"
            name="domain"
            value={formData.domain}
            onChange={handleChange}
            className="w-full mb-4"
            required
          />

          <Label
            className="font-semibold mb-2"
            htmlFor="assignment_category"
            value="Assignment category:"
          />
          <Select
            id="assignment_category"
            inputId="assignment_category"
            options={folderOptions}
            isClearable
            isLoading={foldersLoading}
            onMenuOpen={handleFolderMenuOpen}
            onChange={handleFolderChange}
            value={
              selectedFolderId
                ? folderOptions.find((o) => o.value === selectedFolderId) ?? null
                : null
            }
            placeholder="Select category"
            className="w-full mb-4"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 42,
                borderRadius: 8,
              }),
            }}
          />

          {/* ✅ Disable Create Button After Click */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`bg-primary text-white py-2 px-4 rounded w-full mt-4 ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700"
            }`}
          >
            {isSubmitting ? "Creating..." : "Create"}
          </button>
        </form>
      </div>

      <ToastContainer position="top-right">
        {toasts.map((t) => (
          <ToastUI
            key={t.id}
            type={t.type}
            message={t.message}
            duration={4000}
            onClose={() => removeToast(t.id)}
          />
        ))}
      </ToastContainer>
    </AdminLayout>
  );
};

export default CreateAssignment;
